import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// ── Step card ─────────────────────────────────────────────────────────────────
function TutorialStep({ number, icon, title, desc, detail, tip }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <div style={S.stepCard}>
            <div style={S.stepTop}>
                <div style={S.stepNumBadge}>{number}</div>
                <div style={S.stepIcon}>{icon}</div>
                <div style={S.stepRight}>
                    <div style={S.stepTitle}>{title}</div>
                    <div style={S.stepDesc}>{desc}</div>
                    {detail && (
                        <button style={S.detailToggle} onClick={() => setExpanded(v => !v)}>
                            {expanded ? 'Show less ▴' : 'Show more ▾'}
                        </button>
                    )}
                    {expanded && detail && (
                        <div style={S.detailBox}>{detail}</div>
                    )}
                    {tip && <div style={S.tip}><span style={S.tipLabel}>Tip</span>{tip}</div>}
                </div>
            </div>
        </div>
    );
}

// ── Feature card ──────────────────────────────────────────────────────────────
function FeatureCard({ icon, title, desc, accent = '#bb86fc' }) {
    return (
        <div style={{ ...S.featureCard, borderTopColor: accent }}>
            <div style={{ ...S.featureIcon, color: accent }}>{icon}</div>
            <div style={S.featureTitle}>{title}</div>
            <div style={S.featureDesc}>{desc}</div>
        </div>
    );
}

// ── Tab button ────────────────────────────────────────────────────────────────
function TabBtn({ label, active, onClick }) {
    return (
        <button
            onClick={onClick}
            style={{
                ...S.tabBtn,
                color: active ? '#121212' : '#aaa',
                backgroundColor: active ? '#bb86fc' : 'transparent',
                borderColor: active ? '#bb86fc' : '#444',
            }}
        >
            {label}
        </button>
    );
}

// ── For Fans tab ──────────────────────────────────────────────────────────────
function ForFans() {
    return (
        <div>
            <p style={S.tabIntro}>
                SHARC's My Shows lets you build a personal archive of every concert you've attended.
                Mark shows, log which artists you caught, and explore your history through stats, charts, and a map.
            </p>

            <div style={S.featureGrid}>
                <FeatureCard icon="✅" title="Mark Shows Attended"
                    desc="Any event in the archive can be marked as attended. Your history builds automatically as you log shows."
                    accent="#bb86fc" />
                <FeatureCard icon="🎸" title="Track Artists Caught"
                    desc="At multi-act shows, log exactly which artists you caught — not just that you were there."
                    accent="#03dac6" />
                <FeatureCard icon="📊" title="Stats & Charts"
                    desc="See your show count, venues visited, years active, and a year-by-year bar chart of your history."
                    accent="#cf6679" />
                <FeatureCard icon="🗺️" title="Geographic Map"
                    desc="Every venue you've attended is plotted on a map — see how far your live music history has taken you."
                    accent="#bb86fc" />
            </div>

            <h3 style={S.subheading}>How to Use It</h3>

            <div style={S.stepsContainer}>
                <TutorialStep number="1" icon="🔐" title="Sign In"
                    desc="My Shows is tied to your SHARC account. Sign in using the profile button in the top-right corner of any page."
                    tip="Your show history is private — only you can see it." />
                <TutorialStep number="2" icon="🔍" title="Find a Show"
                    desc="Use the search bar at the top of any page to find an event, or browse through the Events list."
                    tip="Search by artist name to quickly pull up all their shows in the archive." />
                <TutorialStep number="3" icon="✅" title="Mark as Attended"
                    desc='On any Event detail page, click the "Attended" button in the top-right of the event header. It turns green when active.'
                    detail={
                        <ul style={S.detailList}>
                            <li>The Attended button also appears in the Show History tab on Artist and Venue detail pages</li>
                            <li>Click once to mark attended, click again to remove it</li>
                            <li>Your show count updates instantly in My Shows</li>
                        </ul>
                    }
                    tip="You can mark shows from Artist or Venue detail pages without opening each event individually." />
                <TutorialStep number="4" icon="🎸" title="Log Which Artists You Caught"
                    desc='On the Event detail page, each artist in the lineup has a "Caught" toggle. Use this to record which acts you actually watched.'
                    detail={
                        <ul style={S.detailList}>
                            <li>Useful for festivals and multi-act shows where you may have only caught part of the lineup</li>
                            <li>Caught artists appear in your Artists Watched tab in My Shows</li>
                        </ul>
                    }
                    tip="Marking an artist as Caught does not automatically mark the event as Attended — do both for a complete record." />
                <TutorialStep number="5" icon="📋" title="View Your History in My Shows"
                    desc="Click your profile initials in the top-right, then select My Shows. Your full history loads across six tabs."
                    detail={
                        <div style={S.tabList}>
                            {[
                                { tab: 'Show History', desc: "Chronological list of every show you've attended" },
                                { tab: 'Shows by Year', desc: 'Bar chart of your show count by year' },
                                { tab: 'Venues Visited', desc: 'Every venue ranked by number of visits' },
                                { tab: 'Artists Watched', desc: 'Every artist you\'ve caught live, ranked by times seen' },
                                { tab: 'Top Cities', desc: 'Geographic rollup by country, state, and city' },
                                { tab: 'Shows Map', desc: 'Interactive map with a pin for every venue attended' },
                            ].map((t, i) => (
                                <div key={i} style={S.tabRow}>
                                    <span style={S.tabRowName}>{t.tab}</span>
                                    <span style={S.tabRowDesc}>{t.desc}</span>
                                </div>
                            ))}
                        </div>
                    } />
            </div>

            <h3 style={S.subheading}>Your Stats at a Glance</h3>
            <div style={S.statsExplainer}>
                {[
                    { label: 'Total Shows', desc: "All events you've marked as attended" },
                    { label: 'Venues Visited', desc: 'Unique venues across all your attended shows' },
                    { label: 'First Show', desc: 'The earliest dated event in your history' },
                    { label: 'Most Recent', desc: 'The latest dated event in your history' },
                ].map((s, i) => (
                    <div key={i} style={{ ...S.statRow, borderBottom: i < 3 ? '1px solid #222' : 'none' }}>
                        <span style={S.statLabel}>{s.label}</span>
                        <span style={S.statDesc}>{s.desc}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── For Fans Who Also Perform tab ─────────────────────────────────────────────
function ForPerformers() {
    return (
        <div>
            <p style={S.tabIntro}>
                If you're an artist who also attends shows, SHARC lets you link your user account to a
                Member record. Once linked, your show history combines both worlds — performances you played
                and shows you attended — all in one place.
            </p>

            <div style={S.featureGrid}>
                <FeatureCard icon="🔗" title="Link Your Member Record"
                    desc="Connect your SHARC user account to the Member profile that represents you as a performing artist."
                    accent="#bb86fc" />
                <FeatureCard icon="🎤" title="Performed Badge"
                    desc="Any show where your linked member performed automatically appears in My Shows with a purple 'Performed' badge."
                    accent="#cf6679" />
                <FeatureCard icon="📋" title="Combined History"
                    desc="Shows you attended and shows you performed at are unified in a single Show History view."
                    accent="#03dac6" />
                <FeatureCard icon="📊" title="Full Stats"
                    desc="Your stats and charts reflect both attended and performed shows — the complete picture of your live music life."
                    accent="#bb86fc" />
            </div>

            <h3 style={S.subheading}>How to Link Your Member Record</h3>

            <div style={S.stepsContainer}>
                <TutorialStep number="1" icon="👤" title="Go to My Profile"
                    desc="Click your profile initials in the top-right corner and select My Profile from the dropdown."
                    tip="Your profile is where your display name, hometown, and member link all live." />
                <TutorialStep number="2" icon="🔍" title="Find Your Member Record"
                    desc="In My Profile, use the Member search field to find the SHARC member record that represents you. This is the same record that appears on Artist pages under the Members tab."
                    tip="If your member record doesn't exist yet, add it first via Add → Member before linking it here." />
                <TutorialStep number="3" icon="🔗" title="Link It"
                    desc="Select your member record and save your profile. SHARC will now associate your user account with that member."
                    detail={
                        <ul style={S.detailList}>
                            <li>The link is stored on your user profile — it doesn't change the member record itself</li>
                            <li>You can update or remove the link at any time from My Profile</li>
                            <li>Only one member record can be linked per user account</li>
                        </ul>
                    } />
                <TutorialStep number="4" icon="🎤" title="Your Performances Appear Automatically"
                    desc="Once linked, any event where your member is listed in the artist lineup will appear in My Shows with a 'Performed' badge — no manual entry needed."
                    detail={
                        <ul style={S.detailList}>
                            <li>Performed shows appear in your Show History tab alongside attended shows</li>
                            <li>The Shows by Year chart includes both attended and performed show counts</li>
                            <li>Venues where you performed appear in your Venues Visited tab</li>
                            <li>The map plots both attended and performed venue locations</li>
                        </ul>
                    }
                    tip="You can also mark a performed show as Attended if you watched the other acts — both badges will show." />
                <TutorialStep number="5" icon="📋" title="Review Your Combined History"
                    desc="Go to My Shows to see the full picture. Show History will display role badges — green 'Attended' for shows you watched, purple 'Performed' for shows you played."
                    tip="Use the Artists Watched tab to see which acts you've caught as a fan, separate from your own performances." />
            </div>

            <h3 style={S.subheading}>How Badges Work</h3>
            <div style={S.statsExplainer}>
                {[
                    { label: '✅ Attended', desc: "You manually marked this show as attended — you were in the crowd", color: '#4caf50' },
                    { label: '🎤 Performed', desc: "Your linked member record is in the lineup for this event — auto-detected", color: '#bb86fc' },
                ].map((s, i) => (
                    <div key={i} style={{ ...S.statRow, borderBottom: i < 1 ? '1px solid #222' : 'none' }}>
                        <span style={{ ...S.statLabel, color: s.color }}>{s.label}</span>
                        <span style={S.statDesc}>{s.desc}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MyShowsTutorial() {
    const [activeTab, setActiveTab] = useState('fans');

    return (
        <div style={S.page}>

            {/* ── Back to Home ───────────────────────────────────────────── */}
            <div style={S.backBar}>
                <Link to="/" style={S.backLink}>← Back to Home</Link>
            </div>

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <div style={S.hero}>
                <div style={S.heroGlow} />
                <div style={S.heroContent}>
                    <div style={S.eyebrow}>
                        <span style={S.eyebrowPurple}>sharc.fm</span>
                        <span style={S.eyebrowDiv}> | </span>
                        <span style={S.eyebrowSub}>My Show History</span>
                    </div>
                    <h1 style={S.heroTitle}>Your Personal Concert Archive</h1>
                    <p style={S.heroSubtitle}>
                        Track every show you've attended, log which artists you caught,
                        and build a lifetime record of your live music history.
                    </p>
                    <Link to="/my-shows" style={S.heroCta}>Go to My Shows →</Link>
                </div>
            </div>

            <div style={S.body}>

                {/* ── Tabs ───────────────────────────────────────────────── */}
                <div style={S.tabBar}>
                    <TabBtn label="🎟️  For Fans" active={activeTab === 'fans'} onClick={() => setActiveTab('fans')} />
                    <TabBtn label="🎤  For Fans Who Also Perform" active={activeTab === 'performers'} onClick={() => setActiveTab('performers')} />
                </div>

                <div style={S.tabContent}>
                    {activeTab === 'fans' ? <ForFans /> : <ForPerformers />}
                </div>

                {/* ── CTA ────────────────────────────────────────────────── */}
                <section style={S.cta}>
                    <h2 style={S.ctaTitle}>Ready to build your archive?</h2>
                    <p style={S.ctaSubtitle}>Start by finding a show you've attended.</p>
                    <div style={S.ctaButtons}>
                        <Link to="/my-shows" style={S.ctaPrimary}>Go to My Shows</Link>
                        <Link to="/events" style={S.ctaGhost}>Browse Events</Link>
                        <Link to="/how-it-works" style={S.ctaGhost}>How It Works</Link>
                    </div>
                </section>

            </div>
        </div>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    page: {
        backgroundColor: '#121212', color: '#e0e0e0', minHeight: '100vh',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
    },
    hero: {
        position: 'relative', overflow: 'hidden',
        padding: '72px 40px 56px', textAlign: 'center',
        borderBottom: '1px solid #2a2a2a',
    },
    heroGlow: {
        position: 'absolute', top: '-60px', left: '50%',
        transform: 'translateX(-50%)', width: '600px', height: '300px',
        background: 'radial-gradient(ellipse at center, rgba(3,218,198,0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    heroContent: { position: 'relative', zIndex: 2, maxWidth: '640px', margin: '0 auto' },
    eyebrow: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '8px', marginBottom: '20px', fontSize: '13px',
        letterSpacing: '0.08em', textTransform: 'uppercase',
    },
    eyebrowPurple: { color: '#bb86fc', fontWeight: 700 },
    eyebrowDiv: { color: '#555' },
    eyebrowSub: { color: '#888' },
    heroTitle: {
        fontSize: 'clamp(32px, 6vw, 52px)', fontWeight: 800,
        color: '#f0f0f0', margin: '0 0 16px', letterSpacing: '-0.02em',
    },
    heroSubtitle: { fontSize: '16px', lineHeight: 1.7, color: '#aaa', margin: '0 0 28px' },
    heroCta: {
        display: 'inline-block', padding: '12px 28px',
        backgroundColor: '#bb86fc', color: '#121212',
        fontWeight: 700, fontSize: '15px', borderRadius: '6px', textDecoration: 'none',
    },

    backBar: {
        padding: '12px 24px',
        borderBottom: '1px solid #1e1e1e',
    },
    backLink: {
        fontSize: '13px',
        color: '#888',
        textDecoration: 'none',
        fontWeight: 500,
        letterSpacing: '0.02em',
    },

    body: { maxWidth: '820px', margin: '0 auto', padding: '40px 24px 80px' },

    // Tabs
    tabBar: {
        display: 'flex', gap: '10px', marginBottom: '36px',
        borderBottom: '1px solid #2a2a2a', paddingBottom: '16px',
        justifyContent: 'center',
    },
    tabBtn: {
        padding: '10px 20px', borderRadius: '6px', border: '1px solid #444',
        fontSize: '14px', fontWeight: 600, cursor: 'pointer',
        transition: 'all 0.15s', whiteSpace: 'nowrap',
    },
    tabContent: {},

    tabIntro: { fontSize: '15px', lineHeight: 1.75, color: '#aaa', margin: '0 0 32px' },

    subheading: {
        fontSize: '18px', fontWeight: 700, color: '#f0f0f0',
        margin: '40px 0 16px', letterSpacing: '-0.01em',
    },

    featureGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: '14px', marginBottom: '8px',
    },
    featureCard: {
        backgroundColor: '#1a1a1a', border: '1px solid #2e2e2e',
        borderTop: '3px solid #bb86fc', borderRadius: '10px', padding: '20px 16px',
    },
    featureIcon: { fontSize: '24px', marginBottom: '8px' },
    featureTitle: { fontSize: '13px', fontWeight: 700, color: '#e0e0e0', marginBottom: '6px' },
    featureDesc: { fontSize: '12px', color: '#888', lineHeight: 1.6 },

    stepsContainer: { display: 'flex', flexDirection: 'column', gap: '8px' },
    stepCard: {
        backgroundColor: '#1a1a1a', border: '1px solid #2e2e2e',
        borderRadius: '10px', padding: '18px 20px',
    },
    stepTop: { display: 'flex', alignItems: 'flex-start', gap: '12px' },
    stepNumBadge: {
        flexShrink: 0, width: '26px', height: '26px', borderRadius: '50%',
        backgroundColor: '#bb86fc', color: '#121212', fontWeight: 800, fontSize: '12px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '1px',
    },
    stepIcon: { fontSize: '20px', flexShrink: 0, marginTop: '1px' },
    stepRight: { flex: 1 },
    stepTitle: { fontSize: '14px', fontWeight: 700, color: '#e0e0e0', marginBottom: '3px' },
    stepDesc: { fontSize: '13px', color: '#aaa', lineHeight: 1.6 },
    detailToggle: {
        background: 'none', border: 'none', color: '#bb86fc',
        fontSize: '12px', cursor: 'pointer', padding: '5px 0 0', fontWeight: 600,
    },
    detailBox: {
        marginTop: '8px', backgroundColor: '#161616', borderRadius: '6px',
        padding: '10px 14px', border: '1px solid #2a2a2a',
    },
    detailList: {
        margin: 0, paddingLeft: '16px',
        display: 'flex', flexDirection: 'column', gap: '5px',
        fontSize: '13px', color: '#aaa', lineHeight: 1.6,
    },
    tip: {
        marginTop: '8px', fontSize: '12px', color: '#888',
        backgroundColor: '#1e1e1e', borderRadius: '6px',
        padding: '7px 12px', border: '1px solid #2a2a2a',
    },
    tipLabel: {
        color: '#03dac6', fontWeight: 700, marginRight: '6px',
        fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em',
    },

    tabList: { display: 'flex', flexDirection: 'column', gap: '5px' },
    tabRow: { display: 'flex', alignItems: 'baseline', gap: '10px' },
    tabRowName: {
        fontSize: '13px', fontWeight: 700, color: '#bb86fc',
        minWidth: '130px', flexShrink: 0,
    },
    tabRowDesc: { fontSize: '13px', color: '#888', lineHeight: 1.5 },

    statsExplainer: {
        backgroundColor: '#1a1a1a', border: '1px solid #2e2e2e',
        borderRadius: '10px', overflow: 'hidden',
    },
    statRow: { display: 'flex', alignItems: 'baseline', gap: '12px', padding: '12px 20px' },
    statLabel: { fontSize: '13px', fontWeight: 700, color: '#e0e0e0', minWidth: '140px', flexShrink: 0 },
    statDesc: { fontSize: '13px', color: '#888', lineHeight: 1.5 },

    cta: {
        marginTop: '56px', textAlign: 'center', padding: '40px 24px',
        backgroundColor: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: '12px',
    },
    ctaTitle: { fontSize: '22px', fontWeight: 700, color: '#f0f0f0', margin: '0 0 8px' },
    ctaSubtitle: { fontSize: '14px', color: '#888', margin: '0 0 24px' },
    ctaButtons: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
    ctaPrimary: {
        display: 'inline-block', padding: '10px 22px', backgroundColor: '#bb86fc',
        color: '#121212', fontWeight: 700, fontSize: '14px', borderRadius: '6px', textDecoration: 'none',
    },
    ctaGhost: {
        display: 'inline-block', padding: '10px 22px', backgroundColor: 'transparent',
        color: '#bb86fc', fontWeight: 600, fontSize: '14px', borderRadius: '6px',
        textDecoration: 'none', border: '1px solid #bb86fc',
    },
};
